import React from "react";
import { toast } from "sonner";
import { ClientFactory } from "@/main/common/ClientFactory";
import { getSafeToastMessageFromError, logApiFailure } from "@/main/common/ApiUtils";
import type { Localizer } from "@/main/react/Types";

/**
 * How long the "Submission rejected." toast offers its Undo. The server accepts an undo for
 * `GroupDiscussionSubmissionService.REJECT_UNDO_WINDOW` (15 s, measured from the reject's `resolved_at`),
 * which is this 10 s plus room for the round trip, so the Undo must never outlive it: an Undo the server
 * then refuses is a control that lies. Keep the two in step.
 */
export const REJECT_UNDO_TOAST_MS = 10_000;

/**
 * The "Submission rejected." toast with its Undo (X-36, round 29 D14). Both leader surfaces that reject a
 * group submission show it: the review dialog and the manage page's submission list.
 *
 * <p>sonner pauses a toast's timer while it is hovered, so `duration` alone cannot hold the lifetime under
 * the server window; the toast is dismissed on a hard timer as well. An undone reject never reaches the
 * author or requester (the server deletes the notices, A20), so success needs no second toast: the caller's
 * `onUndone` puts the submission back where the leader was looking. A refusal (window closed, a demoted
 * leader) reuses the generic error copy; the server's own message wins when it is a safe one.
 */
export function showSubmissionRejectedToast({
    localizer,
    submissionPublicId,
    onUndone,
}: {
    localizer: Localizer;
    submissionPublicId: string;
    onUndone?: () => void;
}) {
    const undo = async () => {
        try {
            await ClientFactory.getAjaxGroupRestControllerApi().undoRejectDiscussionSubmission(submissionPublicId);
            onUndone?.();
        } catch (error: unknown) {
            logApiFailure("Failed undoing a group discussion submission reject", error);
            toast.error(getSafeToastMessageFromError(error, localizer("typescript.discussion.server_error_generic")));
        }
    };

    const toastId = toast.success(localizer("group.discussion_submission.reject.success"), {
        duration: REJECT_UNDO_TOAST_MS,
        action: {
            // The stable hook rides inside the label (the unsave-undo precedent), so a spec can press it
            // without depending on the localized word.
            label: <span data-playwright-id="group-discussion-submission-reject-undo-action">{localizer("literal.undo")}</span>,
            onClick: () => { void undo(); },
        },
    });
    window.setTimeout(() => toast.dismiss(toastId), REJECT_UNDO_TOAST_MS);
}
